import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PasswordMatchedValidators } from '../../core_modules/common/validators/password-match-validators';
import {UserService} from "../../feature_modules/services/user.service";

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  standalone: true
})
export class ChangePasswordComponent implements OnInit {
  forgotPasswordForm: UntypedFormGroup = new UntypedFormGroup({});
  specialCharacters: any = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
  submitted: boolean = false;
  loading: boolean = false;
  otpSent: boolean = false;
  sendingOtp: boolean = false;
  showPassword: Array<any> = new Array<any>();
  countdown: number = 0;
  countdownInterval: any;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private location: Location,
    private userService: UserService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.forgotPasswordForm = this.formBuilder.group(
      {
        otpCode: [
          { value: null, disabled: !this.otpSent },
          Validators.compose([
            Validators.required,
            Validators.minLength(4),
            Validators.maxLength(6),
          ]),
        ],
        newPassword: [
          { value: null, disabled: !this.otpSent },
          Validators.compose([
            Validators.required,
            Validators.minLength(8),
            PasswordMatchedValidators.patternValidator(/\d/, {
              hasNumber: true,
            }),
            PasswordMatchedValidators.patternValidator(/[A-Z]/, {
              hasCapitalCase: true,
            }),
            PasswordMatchedValidators.patternValidator(/[a-z]/, {
              hasSmallCase: true,
            }),
            PasswordMatchedValidators.patternValidator(this.specialCharacters, {
              hasSpecialCharacters: true,
            }),
          ]),
        ],
        confirmPassword: [
          { value: null, disabled: !this.otpSent },
          Validators.compose([Validators.required, Validators.minLength(8)]),
        ],
      },
      {
        validators: [
          PasswordMatchedValidators.match('newPassword', 'confirmPassword'),
        ],
      }
    );
  }

  onSendOtp(): void {
    if (this.sendingOtp || this.countdown > 0) {
      return;
    }

    this.sendingOtp = true;
    this.userService.sendUserOtp().subscribe({
      next: (response: any) => {
        this.otpSent = true;
        this.enableFormFields();
        this.startCountdown(60); // 60 seconds countdown
      },
      error: (err: any) => {
        this.sendingOtp = false;
      },
      complete: () => {
        this.sendingOtp = false;
      },
    });
  }

  onResendOtp(): void {
    this.onSendOtp();
  }

  onChangePassword(): void {
    // Mark all fields as touched to show validation errors
    Object.keys(this.forgotPasswordForm.controls).forEach(key => {
      this.forgotPasswordForm.controls[key].markAsDirty();
      this.forgotPasswordForm.controls[key].updateValueAndValidity();
    });

    if (this.forgotPasswordForm.valid) {
      this.submitted = true;

      const params = {
        otpCode: this.form['otpCode'].value,
        newPassword: this.form['newPassword'].value,
      };

      this.userService.changePasswordWithOtp(params, true).subscribe({
        next: (response: any) => {
          // Success handled by service
        },
        error: (err: any) => {
          this.submitted = false;
        },
        complete: () => {
          this.submitted = false;
          this.router.navigate(['/login']); // Redirect to login after success
        },
      });
    }
  }

  enableFormFields(): void {
    this.form['otpCode'].enable();
    this.form['newPassword'].enable();
    this.form['confirmPassword'].enable();
  }

  startCountdown(seconds: number): void {
    this.countdown = seconds;
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  onCancelAndBack(): void {
    this.location.back();
  }

  get form(): { [key: string]: AbstractControl } {
    return this.forgotPasswordForm.controls;
  }

  onPasswordToggle(i: number): void {
    this.showPassword[i] = !this.showPassword[i];
  }

  disablePaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
}
